import React, { useEffect, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import productsDb from "../../../../mocks/db/products.db";
import Table, { TBody, Td, TFoot, Th, THead, Tr } from "../../../../components/ui/Table";
import Icon from "../../../../components/icon/Icon";
import { CardBody, CardHeader, CardHeaderChild, CardTitle } from "../../../../components/ui/Card";
import { TableCardFooterTemplate } from "../../../../templates/common/TableParts.template";
import TableSkeletonLoader from "../../../../components/loader/skeleton/tableSkeletonLoader";
import AddMachineModel from "../../../../components/admin/home/addMachineModel";
import Button from "../../../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import Tooltip from "../../../../components/ui/Tooltip";

const defaultData = [productsDb[0], productsDb[1], productsDb[2]];

const columnHelper = createColumnHelper();

const TablePartial = ({ isLoading, machineData, pagination, setPageSize, setPageIndex, handleResetPagination }) => {
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  const [modalStatus, setModalStatus] = useState(false);
  const [editMachineInfo, setEditMachineInfo] = useState({});

  useEffect(() => {
    if (!modalStatus) setEditMachineInfo({});
  }, [modalStatus]);

  const columns = [
    columnHelper.accessor("LocationName", {
      cell: (info) => (
        <Tooltip text={info?.row?.original?.LocationName || ""}>
          <div className="text-sm text-center max-w-[200px] truncate">
            {info?.row?.original?.LocationName || "-"}
          </div>
        </Tooltip>
      ),
      header: "Location Name",
      footer: "Location Name",
      headerClassName: "text-center",
      size: 200,
      maxSize: 200,
    }),
    columnHelper.accessor("Address 01", {
      cell: (info) => (
        <Tooltip text={info?.row?.original?.Address1 || ""}>
          <div className="text-sm text-center max-w-[200px] truncate">
            {info?.row?.original?.Address1 || "-"}
          </div>
        </Tooltip>
      ),
      header: "Address 01",
      footer: "Address 01",
      size: 200,
      maxSize: 200,
    }),
    columnHelper.accessor("Address 02", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.Address2 || "-"}</div>,
      header: "Address 02",
      footer: "Address 02",
    }),
    columnHelper.accessor("City", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.City || "-"}</div>,
      header: "City",
      footer: "City",
    }),
    columnHelper.accessor("State", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.Province || "-"}</div>,
      header: "State",
      footer: "State",
    }),
    columnHelper.accessor("Postal Code", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.PostalCode || "-"}</div>,
      header: "Postal Code",
      footer: "Postal Code",
    }),
    columnHelper.accessor("Country", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.Country || "-"}</div>,
      header: "Country",
      footer: "Country",
    }),
    columnHelper.accessor("Action", {
      cell: (info) => (
        <div className="text-sm flex text-center  justify-center whitespace-nowrap">
          <div
            className="text-[#0EA5E9]  cursor-pointer"
            onClick={() => {
              // setEditMachineInfo(info?.row?.original);
              // setModalStatus(true);
              // navigate({
              //   pathname: `/admin/edit-machine/${info?.row?.original.AssetId}`,
              //   state: { editMachineInfo: info?.row?.original },
              // });
              navigate(`/admin/edit-location/${info?.row?.original.LocationId}`, {
                state: { editLocationInfo: info?.row?.original },
              });
            }}
          >
            Edit
          </div>
          {/* <div className="text-[#FEBA06] pl-[5px]">Delete</div> */}
        </div>
      ),
      header: "Action",
      footer: "Action",
      size: 100,
      minSize: 100,
      meta: {
        sticky: "right",
      },
    }),
  ];

  const handleCloseMachinePopUp = () => {
    setModalStatus(false);
  };

  const table = useReactTable({
    data: machineData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // debugTable: true,
  });

  return (
    <>
      <div className="flex justify-between">
        <CardHeader>
          <CardHeaderChild>
            <CardTitle>My Locations</CardTitle>
          </CardHeaderChild>
        </CardHeader>

        <div className="m-[14px] text-end">
          <Button
            className="!bg-[#FEBA06] border-none !text-black px-[20px] py-[12px] rounded-xl font-medium"
            onClick={() => navigate(`/admin/edit-location`)}
          >
            Add Location
          </Button>
        </div>
      </div>
      <CardBody className="overflow-auto">
        <div className="w-full">
          <div className="overflow-x-auto">
            <Table className="min-w-[1200px]">
              <THead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header?.id}
                        isColumnBorder={false}
                        className={`text-left ${header.column.columnDef.meta?.sticky === 'right' ? '!bg-gray-50 dark:!bg-[#09090b] shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.3)]' : ''}`}
                        style={{
                          width: header.getSize(),
                          ...(header.column.columnDef.meta?.sticky === 'right' && {
                            position: 'sticky',
                            right: 0,
                            zIndex: 10,
                          })
                        }}
                      >
                        {header?.isPlaceholder ? null : (
                          <div
                            key={header.id}
                            aria-hidden="true"
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none flex items-center justify-center  text-center"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: <Icon icon="HeroChevronUp" className="ltr:ml-1.5 rtl:mr-1.5" />,
                              desc: <Icon icon="HeroChevronDown" className="ltr:ml-1.5 rtl:mr-1.5" />,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </THead>
              <TBody>
                {!isLoading ? (
                  Number(pagination?.totalRecords) <= 0 ? (
                    <Tr>
                      <Td className="text-center py-[100px] font-normal text-[30px]" colspan="11">
                        No Record Found
                      </Td>
                    </Tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <Tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <Td
                            key={cell.id}
                            style={{
                              ...(cell.column.columnDef.meta?.sticky === 'right' && {
                                position: 'sticky',
                                right: 0,
                                zIndex: 10,
                              })
                            }}
                            className={cell.column.columnDef.meta?.sticky === 'right' ? 'shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.3)] !bg-white group-even/Tr:!bg-zinc-100 dark:!bg-[#18181b] dark:group-even/Tr:!bg-[#121214]' : ''}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Td>
                        ))}
                      </Tr>
                    ))
                  )
                ) : (
                  [...Array(pagination?.showRecords)].map((_, index) => (
                    <Tr className="bg-gray-100">
                      <TableSkeletonLoader length={8} />
                    </Tr>
                  ))
                )}
              </TBody>
              <TFoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <Tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        isColumnBorder={false}
                        className={`text-left ${header.column.columnDef.meta?.sticky === 'right' ? '!bg-gray-50 dark:!bg-[#09090b] shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.3)]' : ''}`}
                        style={{
                          width: header.getSize(),
                          ...(header.column.columnDef.meta?.sticky === 'right' && {
                            position: 'sticky',
                            right: 0,
                            zIndex: 10,
                          })
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            key={header.id}
                            aria-hidden="true"
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none flex items-center  justify-center"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: <Icon icon="HeroChevronUp" className="ltr:ml-1.5 rtl:mr-1.5" />,
                              desc: <Icon icon="HeroChevronDown" className="ltr:ml-1.5 rtl:mr-1.5" />,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </TFoot>
            </Table>
          </div>
        </div>
      </CardBody >
      <TableCardFooterTemplate
        table={table}
        pagination={pagination}
        setPageSize={setPageSize}
        setPageIndex={setPageIndex}
      />

      {/* Edit Machine Model */}

      <AddMachineModel
        handleResetPagination={handleResetPagination}
        handleCloseMachinePopUp={handleCloseMachinePopUp}
        modalStatus={modalStatus}
        setModalStatus={setModalStatus}
        editMachineInfo={editMachineInfo}
      />
    </>
  );
};

export default TablePartial;
